import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ShieldCheck, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@shared/lib/cn';
import { Container } from '@ui/Container';
import { Button } from '@ui/Button';
import { Logo } from './Logo';
import { ToastProvider } from '@ui/Toast';
import { TooltipProvider } from '@ui/Tooltip';

const NAV = [
  { to: '/fahrzeuge', label: 'Fahrzeuge' },
  { to: '/lernen', label: 'Lernen' },
  { to: '/suche', label: 'Suche', icon: Search },
];

export function AppShell() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <TooltipProvider>
      <ToastProvider>
        <div className="flex min-h-screen flex-col bg-bg-0">
          <header className="sticky top-0 z-40 border-b border-line-subtle/10 bg-bg-0/80 backdrop-blur-md">
            <Container className="flex h-16 items-center justify-between">
              <Link to="/" className="group flex items-center gap-2.5">
                <Logo className="h-8 w-8 text-accent-500 transition-transform group-hover:scale-105" />
                <div className="leading-tight">
                  <div className="text-sm font-semibold tracking-tight text-fg-hi">FFW Arberg</div>
                  <div className="text-2xs uppercase tracking-[0.18em] text-fg-lo">Fahrzeugkunde</div>
                </div>
              </Link>

              <nav className="hidden items-center gap-1 md:flex">
                {NAV.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive ? 'bg-bg-2 text-fg-hi' : 'text-fg-md hover:bg-bg-2 hover:text-fg-hi'
                      )
                    }
                  >
                    {item.icon ? <item.icon className="h-4 w-4" /> : null}
                    {item.label}
                  </NavLink>
                ))}
              </nav>

              <div className="hidden items-center gap-2 md:flex">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/admin">
                    <ShieldCheck className="h-4 w-4" />
                    Admin
                  </Link>
                </Button>
              </div>

              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-fg-md md:hidden"
                onClick={() => setOpen(o => !o)}
                aria-label="Navigation"
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </Container>

            <AnimatePresence>
              {open ? (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden border-t border-line-subtle/10 md:hidden"
                >
                  <Container className="py-3">
                    <nav className="flex flex-col gap-1">
                      {NAV.map(item => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium',
                              isActive ? 'bg-bg-2 text-fg-hi' : 'text-fg-md hover:bg-bg-2'
                            )
                          }
                        >
                          {item.label}
                        </NavLink>
                      ))}
                      <Link
                        to="/admin"
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-fg-md hover:bg-bg-2"
                      >
                        <ShieldCheck className="h-4 w-4" />
                        Admin
                      </Link>
                    </nav>
                  </Container>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </header>

          <main className="flex-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={pathname}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </main>

          <footer className="border-t border-line-subtle/10 bg-bg-0">
            <Container className="flex flex-col gap-2 py-6 text-xs text-fg-lo md:flex-row md:items-center md:justify-between">
              <div>© {new Date().getFullYear()} Freiwillige Feuerwehr Arberg</div>
              <div className="flex items-center gap-4">
                <Link to="/impressum" className="hover:text-fg-md">Impressum</Link>
                <span className="opacity-50">·</span>
                <span>Lernsystem in Aufbau</span>
              </div>
            </Container>
          </footer>
        </div>
      </ToastProvider>
    </TooltipProvider>
  );
}
